import { type ChangeEvent, type RefObject } from 'react';
import type { QRCodeConfig, ErrorCorrectionLevel, DotStyle } from '../types';
import { ERROR_CORRECTION_LEVELS, DOT_STYLES } from '../types';

interface QROptionsProps {
  config: QRCodeConfig;
  onChange: (updates: Partial<QRCodeConfig>) => void;
  logoInputRef: RefObject<HTMLInputElement | null>;
}

export function QROptions({ config, onChange, logoInputRef }: QROptionsProps) {
  const handleColor = (field: 'foregroundColor' | 'backgroundColor') => {
    return (e: ChangeEvent<HTMLInputElement>) => onChange({ [field]: e.target.value });
  };

  const handleDotStyle = (style: DotStyle) => onChange({ dotStyle: style });
  const handleECLevel = (level: ErrorCorrectionLevel) => onChange({ errorCorrectionLevel: level });
  const handleMargin = (e: ChangeEvent<HTMLInputElement>) => onChange({ margin: Number(e.target.value) });
  const handleLogoSize = (e: ChangeEvent<HTMLInputElement>) => onChange({ logoSize: Number(e.target.value) });

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ logoDataUrl: ev.target?.result as string });
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    onChange({ logoDataUrl: undefined });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  return (
    <div className="qr-options">
      <div className="options-grid">
        <h3 className="section-title">美化选项</h3>

        {/* Foreground Color */}
        <div className="option-group">
          <label className="option-label">前景色</label>
          <div className="color-picker-row">
            <input type="color" value={config.foregroundColor} onChange={handleColor('foregroundColor')} className="color-input" />
            <span className="color-value">{config.foregroundColor}</span>
          </div>
        </div>

        {/* Background Color */}
        <div className="option-group">
          <label className="option-label">背景色</label>
          <div className="color-picker-row">
            <input type="color" value={config.backgroundColor} onChange={handleColor('backgroundColor')} className="color-input" />
            <span className="color-value">{config.backgroundColor}</span>
          </div>
        </div>

        {/* Dot Style */}
        <div className="option-group">
          <label className="option-label">点阵样式</label>
          <div className="btn-group">
            {DOT_STYLES.map((s) => (
              <button key={s.value} className={`btn btn-sm ${config.dotStyle === s.value ? 'btn-active' : ''}`}
                onClick={() => handleDotStyle(s.value)} type="button">{s.label}</button>
            ))}
          </div>
        </div>

        {/* Error Correction */}
        <div className="option-group">
          <label className="option-label">纠错等级</label>
          <div className="btn-group">
            {ERROR_CORRECTION_LEVELS.map((l) => (
              <button key={l.value} className={`btn btn-sm ${config.errorCorrectionLevel === l.value ? 'btn-active' : ''}`}
                onClick={() => handleECLevel(l.value)} title={l.description} type="button">{l.value}</button>
            ))}
          </div>
        </div>

        {/* Margin */}
        <div className="option-group">
          <label className="option-label">边距</label>
          <div className="range-row">
            <input type="range" min={0} max={8} step={1} value={config.margin} onChange={handleMargin} className="range-input" />
            <span className="range-value">{config.margin}</span>
          </div>
        </div>

        {/* Logo */}
        <div className="option-group">
          <label className="option-label">中心 Logo</label>
          <div className="logo-controls">
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="file-input" />
            <button className="btn btn-sm" onClick={() => logoInputRef.current?.click()} type="button">选择</button>
            {config.logoDataUrl && (
              <button className="btn btn-sm btn-danger" onClick={handleRemoveLogo} type="button">移除</button>
            )}
            {config.logoDataUrl && (
              <>
                <span style={{ fontSize: '0.72rem', color: 'var(--color-text-secondary)' }}>大小</span>
                <input type="range" min={10} max={40} step={1} value={config.logoSize} onChange={handleLogoSize}
                  style={{ width: 60, verticalAlign: 'middle' }} className="range-input" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
